import type { WorkOrderChangeSeasonData } from 'gql/graphql';

import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { WorkOrderChangeSeasonValueGetter, WorkOrderChangeSeasonValueRenderer } from '../CellRenderers';
import { SEASON_COST_COLUMNS_WIDTH, VOLUME_OTHER_COLUMNS_WIDTH } from '../constants';

interface IUseWorkOrderChangeOtherSeasonsColumns {
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}

export const useWorkOrderChangeOtherSeasonsColumns = (props: IUseWorkOrderChangeOtherSeasonsColumns) => {
    const { workOrderChangeSeasons, costRounding } = props;

    return useMemo(
        () =>
            workOrderChangeSeasons.reduce(
                (acc: { columns: ColDef[]; isOdd: boolean }, item: WorkOrderChangeSeasonData) => {
                    const cellClass = acc.isOdd ? 'oddSeason' : 'evenSeason';

                    const season = [
                        {
                            headerName: `Сезон ${item.year}, план`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    width: VOLUME_OTHER_COLUMNS_WIDTH,
                                    cellClass,
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonId: item.id,
                                        seasonYear: item.year,
                                        dataKey: 'planCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: costRounding,
                                        withErrorCheck: true,
                                    }),
                                    cellClass,
                                },
                            ],
                        },
                        {
                            headerName: `Сезон ${item.year}, выполнено`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    width: VOLUME_OTHER_COLUMNS_WIDTH,
                                    cellClass,
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonId: item.id,
                                        seasonYear: item.year,
                                        dataKey: 'completedCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: costRounding,
                                        withDeviation: true,
                                    }),
                                    cellClass,
                                },
                            ],
                        },
                        {
                            headerName: `Сезон ${item.year}, остаток`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    width: VOLUME_OTHER_COLUMNS_WIDTH,
                                    cellClass,
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonId: item.id,
                                        seasonYear: item.year,
                                        dataKey: 'remainderCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({ decimal: costRounding }),
                                    cellClass,
                                },
                            ],
                        },
                    ];

                    acc.columns.push(...season);
                    acc.isOdd = !acc.isOdd;

                    return acc;
                },
                {
                    columns: [],
                    isOdd: true,
                },
            ).columns,
        [costRounding, workOrderChangeSeasons],
    );
};
